import {
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FuseDrawerModule } from '@fuse/components/drawer';
import { MatTabsModule } from '@angular/material/tabs';
import { MainFuseDrawerService } from '@sbd-account-portal/app/modules/main-fuse-drawer/main-fuse-drawer.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, Subject, takeUntil } from 'rxjs';
import { RiderRequestComponent } from '@sbd-account-portal/app/modules/rider-request/rider-request.component';
import {
  OpenQuoteRequestsItem,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/quote-requests.types';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { User } from '@sbd-account-portal/app/core/user/user.types';
import {
  PurchaseStepsComponent,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/purchase-steps/purchase-steps.component';
import { CathyService } from '@sbd-account-portal/app/services/cathy.service';
import {
  OpenQuoteRequestsWidgetService,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import {
  createDrawerTemplate,
  DrawerBottomBarButton, DrawerTemplate,
} from '@sbd-account-portal/app/modules/main-fuse-drawer/menus-buttons-template.types';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { CIPXQuoteRequestIntakeFormComponent } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/intake-form.component';
import { IntakeFormStep } from '@sbd-doug-frontend/shared/lib/quote/requests/intake/form/intake-form.types';
import { MainFuseDrawerHeaderComponent } from '@sbd-account-portal/app/modules/main-fuse-drawer/main-fuse-drawer-header.component';

@Component({
  selector: 'sbd-account-portal-frontend-mainfusedrawer',
  standalone: true,
  imports: [
    CommonModule,
    FuseDrawerModule,
    MatTabsModule,
    RiderRequestComponent,
    PurchaseStepsComponent,
    MatIcon,
    MatIconButton,
    MainFuseDrawerHeaderComponent,
    CIPXQuoteRequestIntakeFormComponent,
  ],
  templateUrl: './mainfusedrawer.component.html',
  styleUrl: './mainfusedrawer.component.scss',
})
export class MainFuseDrawerComponent implements OnInit, OnDestroy {
  @Input() content = '';
  @Output() updatedOpenQREvent = new EventEmitter<any>();
  @Output() moveBackEvent = new EventEmitter<void>();
  @Output() moveToNextStepEvent = new EventEmitter<void>();
  @Output() getShippingMethodValueEvent = new EventEmitter<any>();
  @ViewChild('fuseDrawerTop') fuseDrawerTop!: ElementRef;

  _formBuilder = inject(FormBuilder);
  _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);
  _mainFuseDrawerService = inject(MainFuseDrawerService);
  _changeDetector = inject(ChangeDetectorRef);
  _cathyService = inject(CathyService);

  _unsubscribeAll: Subject<void> = new Subject<void>();
  openDrawer = false;

  // Purchase steps properties
  step = 1;
  selectedQR: OpenQuoteRequestsItem = null;
  queuedQR: OpenQuoteRequestsItem[] = [];
  subtotal = 0;
  shippingTotal = 0;
  total = 0;
  isPurchasing = false;
  activeOpenedQR: any = {};
  checkoutForm: UntypedFormGroup = null;
  loggedInAccountUser: User = null;
  isPurchased = false;
  recentlyPurchasedCount = 0;
  mainDrawerTemplateConfig: DrawerTemplate;
  showCloseButton = true;
  showMainDrawerCloseButton = false;

  selectedOpenQR$ = toObservable(this._openQuoteRequestsWidgetService.selectedOpenQR).pipe(
    takeUntil(this._unsubscribeAll),
    map((selectedOpenQR) => {
      if (selectedOpenQR && +selectedOpenQR?.quote_request_id > 0) {
        //Pass config to drawer
        this.mainDrawerTemplateConfig = createDrawerTemplate('OpenQR', this, this._mainFuseDrawerService, selectedOpenQR);
      }
      this._changeDetector.detectChanges();
    }),
  ).subscribe();


  isPurchased$ = toObservable(this._mainFuseDrawerService.isPurchased).pipe(
    takeUntil(this._unsubscribeAll),
    map((isPurchased) => {
      this.isPurchased = isPurchased;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  activeOpenedQR$ = toObservable(this._mainFuseDrawerService.activeOpenedQR).pipe(
    takeUntil(this._unsubscribeAll),
    map((activeOpenedQR) => {
      this.activeOpenedQR = activeOpenedQR;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  openDrawer$ = toObservable(this._mainFuseDrawerService.openDrawer).pipe(
    takeUntil(this._unsubscribeAll),
    map((openDrawer: boolean) => {
      this.openDrawer = openDrawer;

      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  riderSubmitted$ = toObservable(this._mainFuseDrawerService.riderSubmitted).pipe(
    takeUntil(this._unsubscribeAll),
    map((riderSubmitted: boolean) => {
      if (riderSubmitted) {
        this._mainFuseDrawerService.contentContext.set('purchase-steps');
        this._mainFuseDrawerService.showStepsHeader.set(true);
      }
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  contentContext$ = toObservable(this._mainFuseDrawerService.contentContext).pipe(
    takeUntil(this._unsubscribeAll),
    map((contentContext: string) => {
      this.content = contentContext;
      if(this.content === 'purchase-steps') {
        this.mainDrawerTemplateConfig = createDrawerTemplate('OpenQR', this, this._mainFuseDrawerService, this.selectedQR);
      } else {
        this.mainDrawerTemplateConfig = null
      }
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  // Subscribe to purchase steps data
  step$ = toObservable(this._mainFuseDrawerService.step).pipe(
    takeUntil(this._unsubscribeAll),
    map((step: number) => {
      this.step = step;

      if (this.step === 5) {
        this.purchaseQuoteRequest();
      }

      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  selectedQR$ = toObservable(this._mainFuseDrawerService.selectedQR).pipe(
    takeUntil(this._unsubscribeAll),
    map((selectedQR: OpenQuoteRequestsItem) => {
      this.selectedQR = selectedQR;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  queuedQR$ = toObservable(this._mainFuseDrawerService.queuedQR).pipe(
    takeUntil(this._unsubscribeAll),
    map((queuedQR: OpenQuoteRequestsItem[]) => {
      // this.queuedQR = queuedQR;
      // console.log('queuedQR', this.queuedQR);
      if(this.queuedQR.length > 0) {
        this.mainDrawerTemplateConfig = null;
      }
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  subtotal$ = toObservable(this._mainFuseDrawerService.subtotal).pipe(
    takeUntil(this._unsubscribeAll),
    map((subtotal: number) => {
      this.subtotal = subtotal;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  shippingTotal$ = toObservable(this._mainFuseDrawerService.shippingTotal).pipe(
    takeUntil(this._unsubscribeAll),
    map((shippingTotal: number) => {
      this.shippingTotal = shippingTotal;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  total$ = toObservable(this._mainFuseDrawerService.total).pipe(
    takeUntil(this._unsubscribeAll),
    map((total: number) => {
      this.total = total;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  checkoutForm$ = toObservable(this._mainFuseDrawerService.checkoutForm).pipe(
    takeUntil(this._unsubscribeAll),
    map((checkoutForm) => {
      this.checkoutForm = checkoutForm;
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  constructor(@Inject(DOCUMENT) private _document: any) {
  }

  ngDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  ngOnInit() {
    fromEvent<KeyboardEvent>(this._document, 'keydown')
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter<KeyboardEvent>(
          (event) =>
            event.key == 'Escape' || (event.altKey && event.code === 'KeyF'),
        ),
      )
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.closeDrawer();
        } else if (event.code === 'KeyF' && event.altKey) {
        }
      });
  }


  onTabChange(event: any): void {
    // Update the content context based on selected tab
    const newContent = event.index === 0 ? 'purchase-steps' : 'rider';
    this._mainFuseDrawerService.contentContext.set(newContent);
  }

  closeDrawer() {
    this._mainFuseDrawerService.openDrawer.set(false);
    this._mainFuseDrawerService.resetPurchaseSteps();
    this._changeDetector.detectChanges();
  }


  updateSelectedQuoteOption(event: any) {
    this._mainFuseDrawerService.updateSelectedQuoteOption(event).subscribe();
    this._changeDetector.markForCheck();
  }

  onMoveBackEvent() {
    const currentStep = this._mainFuseDrawerService.step();
    this._mainFuseDrawerService.step.set(currentStep - 1);
    this._changeDetector.markForCheck();
  }

  onMoveToNextStepEvent() {
    const currentStep = this._mainFuseDrawerService.step();
    this._mainFuseDrawerService.step.set(currentStep + 1);

    if (currentStep + 1 === 2) {
      this._mainFuseDrawerService.reArrangeQR();
    }

    if (this._mainFuseDrawerService.step() === 4) {
      this.handleStep4Validation();
    }

    this._changeDetector.markForCheck();
  }

  onGetShippingMethodValueEvent(event: any) {
    this.getShippingMethodValueEvent.emit(event);
  }

  expressPurchaseQuoteRequest(quoteRequest: OpenQuoteRequestsItem) {
    this.mainDrawerTemplateConfig = null;
    this._openQuoteRequestsWidgetService.addCheckedQuoteRequestIds(+quoteRequest.quote_request_id, true);
    this._mainFuseDrawerService.addOpenQRToQueue(quoteRequest);
    this._mainFuseDrawerService.selectedQR.set(null);
    this._mainFuseDrawerService.reArrangeQR();
    this.showCloseButton = true;
    this._changeDetector.markForCheck();
  }

  private purchaseQuoteRequest() {
    this._mainFuseDrawerService.purchaseQuoteRequest().subscribe();
  }

  private handleStep4Validation(): void {
    this.markCreditCardFieldsAsTouched();

    if (!this.isCreditCardFormValid()) {
      this._cathyService.error('Please fill out the credit card form below.');
      const currentStep = this._mainFuseDrawerService.step();
      this._mainFuseDrawerService.step.set(currentStep - 1);
      return;
    }

    if (!this.checkoutForm.get('useDefaultAddress').value) {
      this.markBillingAddressFieldsAsTouched();
      if (!this.isBillingAddressFormValid()) {
        this._cathyService.error('Please fill out the address required fields in the form below.');
        const currentStep = this._mainFuseDrawerService.step();
        this._mainFuseDrawerService.step.set(currentStep - 1);
        return;
      }
    }
  }

  markCreditCardFieldsAsTouched() {
    const creditCardFields = [
      'payer_name',
      'credit_card_cvv',
      'expiration_date',
      'credit_card_number',
    ];

    creditCardFields.forEach(field => {
      this.checkoutForm.get(field).markAsTouched();
    });
  }

  isCreditCardFormValid(): boolean {
    const creditCardFields = [
      'payer_name',
      'credit_card_cvv',
      'expiration_date',
      'credit_card_number',
    ];

    return creditCardFields.every(field =>
      this.checkoutForm.get(field).value?.trim() !== '' && !this.checkoutForm.get(field).errors,
    );
  }

  markBillingAddressFieldsAsTouched() {
    const billingFields = [
      'address_street_1',
      'address_city',
      'address_state_abbreviation',
      'address_zip',
    ];

    billingFields.forEach(field => {
      this.checkoutForm.get(field).markAsTouched();
    });
  }

  isBillingAddressFormValid(): boolean {
    const billingFields = [
      'address_street_1',
      'address_city',
      'address_state_abbreviation',
      'address_zip',
    ];

    return billingFields.every(field =>
      !this.checkoutForm.get(field).hasError('required'),
    );
  }

  onFooterButtonClickEvent($event: DrawerBottomBarButton) {
    console.log('onFooterButtonClickEvent', $event);
  }


  setContentContext(ctx: string) {
    this._mainFuseDrawerService.contentContext.set(ctx);
  }

  onIntakeFormStepChanged(step: IntakeFormStep) {
    this.fuseDrawerTop.nativeElement.scrollTop = 0;
  }

  onIntakeFormComplete() {
    this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe();
  }
}
